import cx from "classnames";
import { useEffect, useRef } from "react";
import ContainerBoundary from "@/components/common/ContainerBoundary";
import ErrorBoundary from "@/components/empty/ErrorBoundary";
import ScoreBoardContainer from "@/components/scoreboard/ScoreboardContainer";
import MoreNews from "@/components/sport/MoreNews";
import SportArticlesContainer from "@/components/sport/SportArticlesContainer";
import { LEAGUE_PAGE_VIEW } from "@/constants/amplitude";
import { Article, GenericSport } from "@/graphql/gql/graphql";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import useRecentArticles from "@/utils/hooks/useRecentArticles";
import styles from "./sportContainer.module.scss";

interface Props {
  sport: Partial<GenericSport>;
  featuredArticles: Article[];
}

const SportContainer = ({ sport, featuredArticles }: Props) => {
  const {
    articles: moreArticles,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
  } = useRecentArticles({
    limit: 12,
    sportName: sport.name,
    hideDeprioritizedContent: true,
  });

  const { setPage, logAmplitudeEvent, isActive } = useAmplitude();
  const titleRef = useRef("");
  useEffect(() => {
    if (
      titleRef.current !== sport.seo?.title &&
      titleRef.current !== sport.name &&
      isActive
    ) {
      titleRef.current = sport.seo?.title ?? sport.name ?? "";
      setPage("league");
      logAmplitudeEvent(
        LEAGUE_PAGE_VIEW,
        { Competition: sport.name ?? "" },
        { page: "league" }
      );
    }
  }, [setPage, sport.seo?.title, sport.name, logAmplitudeEvent, isActive]);

  let title = `${sport.name} Home`;
  if (sport.homePageTitle?.trim()) {
    title = sport.homePageTitle;
  }

  return (
    <ErrorBoundary>
      <ContainerBoundary legacy>
        <h1 className={styles.heading}>{title}</h1>
        <SportArticlesContainer articles={featuredArticles} />
        <div className={styles.lowerArea}>
          <section className={styles.section}>
            {sport.hasLiveScoring && <ScoreBoardContainer sport={sport.name} />}
            <h2 className={cx(styles.heading, styles.recent)}>
              {`Recent ${sport.name} News`}
            </h2>
            {moreArticles && (
              <MoreNews
                hasMore={!!hasNextPage}
                loadMore={fetchNextPage}
                articles={moreArticles}
                origin="recent_stories"
                isLoading={isLoading || isFetching}
              />
            )}
          </section>
        </div>
      </ContainerBoundary>
    </ErrorBoundary>
  );
};
export default SportContainer;
